<template>
    <div class="flex flex-col dark:text-white">
        <!-- Filtro de Período -->
        <div class="mb-6 rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
            <div class="flex items-center justify-between">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Filtro de Período</h2>
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700 dark:text-gray-300">Mês:</label>
                        <select
                            v-model="selectedMonth"
                            @change="fetchData"
                            class="rounded-lg border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-slate-700 dark:text-white"
                        >
                            <option v-for="month in availableMonths" :key="month.value" :value="month.value">
                                {{ month.label }}
                            </option>
                        </select>
                    </div>
                    <button
                        @click="exportData"
                        :disabled="loading"
                        class="inline-flex items-center justify-center rounded-md bg-green-500 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-green-400"
                    >
                        <font-awesome-icon :icon="['fas', 'file-export']" class="mr-2" />
                        Exportar
                    </button>
                </div>
            </div>
        </div>

        <!-- Cards de Métricas por Idade -->
        <div class="mb-6">
            <h2 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Segmentação por Tempo de Cadastro</h2>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <!-- Primeira Semana -->
                <div class="rounded-lg bg-linear-to-br from-green-50 to-green-100 p-4 dark:from-green-900/20 dark:to-green-800/20">
                    <div class="mb-3 flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'user-plus']" class="h-5 w-5 text-green-600" />
                            <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ ageMetrics.first_week.label }}</span>
                        </div>
                        <span class="rounded-full bg-green-600 px-2 py-1 text-xs font-bold text-white">
                            {{ ageMetrics.first_week.seller_count }}
                        </span>
                    </div>
                    <div class="space-y-2">
                        <div>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Total Comissões</p>
                            <p class="text-xl font-bold text-green-700 dark:text-green-400">{{ formatCurrency(ageMetrics.first_week.total_commissions) }}</p>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Média/Vendedor</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">
                                {{ ageMetrics.first_week.seller_count > 0 ? formatCurrency(ageMetrics.first_week.total_commissions / ageMetrics.first_week.seller_count) : 'R$ 0,00' }}
                            </span>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Transações</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">{{ ageMetrics.first_week.transaction_count }}</span>
                        </div>
                    </div>
                </div>

                <!-- Primeiro Mês -->
                <div class="rounded-lg bg-linear-to-br from-blue-50 to-blue-100 p-4 dark:from-blue-900/20 dark:to-blue-800/20">
                    <div class="mb-3 flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'user-clock']" class="h-5 w-5 text-blue-600" />
                            <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ ageMetrics.first_month.label }}</span>
                        </div>
                        <span class="rounded-full bg-blue-600 px-2 py-1 text-xs font-bold text-white">
                            {{ ageMetrics.first_month.seller_count }}
                        </span>
                    </div>
                    <div class="space-y-2">
                        <div>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Total Comissões</p>
                            <p class="text-xl font-bold text-blue-700 dark:text-blue-400">{{ formatCurrency(ageMetrics.first_month.total_commissions) }}</p>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Média/Vendedor</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">
                                {{ ageMetrics.first_month.seller_count > 0 ? formatCurrency(ageMetrics.first_month.total_commissions / ageMetrics.first_month.seller_count) : 'R$ 0,00' }}
                            </span>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Transações</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">{{ ageMetrics.first_month.transaction_count }}</span>
                        </div>
                    </div>
                </div>

                <!-- Primeiros 6 Meses -->
                <div class="rounded-lg bg-linear-to-br from-yellow-50 to-yellow-100 p-4 dark:from-yellow-900/20 dark:to-yellow-800/20">
                    <div class="mb-3 flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'user-check']" class="h-5 w-5 text-yellow-600" />
                            <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ ageMetrics.first_6_months.label }}</span>
                        </div>
                        <span class="rounded-full bg-yellow-600 px-2 py-1 text-xs font-bold text-white">
                            {{ ageMetrics.first_6_months.seller_count }}
                        </span>
                    </div>
                    <div class="space-y-2">
                        <div>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Total Comissões</p>
                            <p class="text-xl font-bold text-yellow-700 dark:text-yellow-400">{{ formatCurrency(ageMetrics.first_6_months.total_commissions) }}</p>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Média/Vendedor</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">
                                {{ ageMetrics.first_6_months.seller_count > 0 ? formatCurrency(ageMetrics.first_6_months.total_commissions / ageMetrics.first_6_months.seller_count) : 'R$ 0,00' }}
                            </span>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Transações</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">{{ ageMetrics.first_6_months.transaction_count }}</span>
                        </div>
                    </div>
                </div>

                <!-- Estabelecidos -->
                <div class="rounded-lg bg-linear-to-br from-purple-50 to-purple-100 p-4 dark:from-purple-900/20 dark:to-purple-800/20">
                    <div class="mb-3 flex items-center justify-between">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'user-shield']" class="h-5 w-5 text-purple-600" />
                            <span class="text-sm font-semibold text-gray-700 dark:text-gray-300">{{ ageMetrics.established.label }}</span>
                        </div>
                        <span class="rounded-full bg-purple-600 px-2 py-1 text-xs font-bold text-white">
                            {{ ageMetrics.established.seller_count }}
                        </span>
                    </div>
                    <div class="space-y-2">
                        <div>
                            <p class="text-xs text-gray-500 dark:text-gray-400">Total Comissões</p>
                            <p class="text-xl font-bold text-purple-700 dark:text-purple-400">{{ formatCurrency(ageMetrics.established.total_commissions) }}</p>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Média/Vendedor</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">
                                {{ ageMetrics.established.seller_count > 0 ? formatCurrency(ageMetrics.established.total_commissions / ageMetrics.established.seller_count) : 'R$ 0,00' }}
                            </span>
                        </div>
                        <div class="flex justify-between text-xs">
                            <span class="text-gray-500 dark:text-gray-400">Transações</span>
                            <span class="font-medium text-gray-700 dark:text-gray-300">{{ ageMetrics.established.transaction_count }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Métricas de Resumo -->
        <div class="mb-6">
            <h2 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Resumo do Período</h2>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="flex items-center justify-between mb-2">
                        <font-awesome-icon :icon="['fas', 'users']" class="h-5 w-5 text-indigo-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Total</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ summaryMetrics.total_sellers }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Vendedores Ativos</p>
                </div>

                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="flex items-center justify-between mb-2">
                        <font-awesome-icon :icon="['fas', 'money-bill-wave']" class="h-5 w-5 text-green-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Comissões</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ formatCurrency(summaryMetrics.total_commissions) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Total no Período</p>
                </div>

                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="flex items-center justify-between mb-2">
                        <font-awesome-icon :icon="['fas', 'chart-line']" class="h-5 w-5 text-blue-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Média</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ formatCurrency(summaryMetrics.avg_commission_per_seller) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Por Vendedor</p>
                </div>

                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="flex items-center justify-between mb-2">
                        <font-awesome-icon :icon="['fas', 'receipt']" class="h-5 w-5 text-orange-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Transações</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ formatNumber(summaryMetrics.total_transactions) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Total no Período</p>
                </div>

                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="flex items-center justify-between mb-2">
                        <font-awesome-icon :icon="['fas', 'percentage']" class="h-5 w-5" :class="summaryMetrics.commission_variation >= 0 ? 'text-green-600' : 'text-red-600'" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Variação</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <p class="text-2xl font-bold" :class="summaryMetrics.commission_variation >= 0 ? 'text-green-600' : 'text-red-600'">
                            {{ summaryMetrics.commission_variation > 0 ? '+' : '' }}{{ summaryMetrics.commission_variation }}%
                        </p>
                        <font-awesome-icon
                            :icon="['fas', summaryMetrics.commission_variation >= 0 ? 'arrow-up' : 'arrow-down']"
                            :class="summaryMetrics.commission_variation >= 0 ? 'text-green-500' : 'text-red-500'"
                        />
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400">vs Mês Anterior</p>
                </div>
            </div>
        </div>

<!-- Tabela de Vendedores -->
        <table-component
            :loading="loading"
            :pagination="pagination"
            :headers="headers"
            @fetch="fetchData"
        >
            <tr v-for="seller in sellers" :key="seller.id">
                <td class="table-cell-sm">
                    <div>
                        <div class="font-medium text-gray-900 dark:text-white">{{ seller.email }}</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">ID: {{ seller.id }}</div>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <div>
                        <div class="text-sm text-gray-900 dark:text-white">{{ formatDate(seller.registered_at) }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ seller.days_since_registration }} dias</div>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <span
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        :class="getAgeGroupClass(seller.age_group)"
                    >
                        {{ getAgeGroupLabel(seller.age_group) }}
                    </span>
                </td>
                <td class="table-cell-sm">
                    <span
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        :class="{
                            'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': seller.score >= 80,
                            'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': seller.score >= 40 && seller.score < 80,
                            'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': seller.score < 40
                        }"
                    >
                        {{ seller.score }}
                    </span>
                </td>
                <td class="table-cell-sm text-right">
                    <div>
                        <div class="font-medium text-gray-900 dark:text-white">{{ formatCurrency(seller.total_commissions) }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">{{ seller.transaction_count }} transações</div>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <span
                        v-if="seller.audit_status"
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        :class="getAuditStatusClass(seller.audit_status)"
                    >
                        {{ getAuditStatusLabel(seller.audit_status) }}
                    </span>
                    <span v-else class="text-xs text-gray-500 dark:text-gray-400">-</span>
                </td>
            </tr>
        </table-component>
    </div>
</template>

<script>
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import axios from 'axios';
import { useToast } from "vue-toastification";

export default {
    components: {
        TableComponent,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            selectedMonth: '',
            availableMonths: [],
            ageMetrics: {
                first_week: { seller_count: 0, total_commissions: 0, avg_commission: 0, transaction_count: 0, label: '0-7 dias' },
                first_month: { seller_count: 0, total_commissions: 0, avg_commission: 0, transaction_count: 0, label: '8-30 dias' },
                first_6_months: { seller_count: 0, total_commissions: 0, avg_commission: 0, transaction_count: 0, label: '31-180 dias' },
                established: { seller_count: 0, total_commissions: 0, avg_commission: 0, transaction_count: 0, label: '180+ dias' }
            },
            summaryMetrics: {
                total_sellers: 0,
                total_commissions: 0,
                avg_commission_per_seller: 0,
                total_transactions: 0,
                commission_variation: 0
            },
            sellers: [],
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 10,
            },
            period: {
                start: '',
                end: '',
                month_label: ''
            }
        }
    },
    computed: {
        headers() {
            return [
                "Vendedor",
                "Data Cadastro",
                "Segmento",
                "Score Risco",
                "Comissões",
                "Status Audit"
            ];
        }
    },
    created() {
        // Inicializar meses antes de montar o componente
        this.initializeMonths();
    },
    mounted() {
        // Carregar dados imediatamente ao montar
        this.fetchData();
    },
    methods: {
        initializeMonths() {
            const months = [];
            const currentDate = new Date();

            for (let i = 0; i < 12; i++) {
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
                const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
            }

            this.availableMonths = months;
            this.selectedMonth = months[0].value;
        },
        fetchData(page = 1) {
            this.loading = true;
            this.pagination.current_page = page;

            const [year, month] = this.selectedMonth.split('-');
            const monthStart = `${year}-${month}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const monthEnd = `${year}-${month}-${lastDay}`;

            // Fazer requisições em paralelo para melhor performance
            Promise.all([
                // Requisição 1: Métricas por idade
                axios.get('/api/seller-age-analysis/metrics', {
                    params: {
                        month_start: monthStart,
                        month_end: monthEnd
                    }
                }),
                // Requisição 2: Lista de vendedores
                axios.get('/api/seller-age-analysis/sellers', {
                    params: {
                        month_start: monthStart,
                        month_end: monthEnd,
                        page: this.pagination.current_page,
                        per_page: this.pagination.per_page
                    }
                }),
                // Requisição 3: Sumário
                axios.get('/api/seller-age-analysis/summary', {
                    params: {
                        month_start: monthStart,
                        month_end: monthEnd
                    }
                })
            ])
            .then(([metricsResponse, sellersResponse, summaryResponse]) => {
                // Processar métricas
                const metricsData = metricsResponse.data.data;
                this.ageMetrics = metricsData.age_metrics;
                this.period = metricsData.period;

                // Processar vendedores
                const sellersData = sellersResponse.data.data;
                this.sellers = sellersData.data;
                this.pagination = {
                    ...this.pagination,
                    total: sellersData.total,
                    last_page: sellersData.last_page
                };

                // Processar sumário
                this.summaryMetrics = summaryResponse.data.data;
            })
            .catch(error => {
                console.error('Erro ao carregar dados:', error);
                this.toast.error("Erro ao carregar dados da análise");
            })
            .finally(() => {
                this.loading = false;
            });
        },
        exportData() {
            this.loading = true;

            const [year, month] = this.selectedMonth.split('-');
            const monthStart = `${year}-${month}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const monthEnd = `${year}-${month}-${lastDay}`;

            axios.post('/api/seller-age-analysis/export', {
                month_start: monthStart,
                month_end: monthEnd
            })
            .then(response => {
                const data = response.data.data;
                const filename = response.data.filename;
                this.downloadCSV(data, filename);
                this.toast.success("Dados exportados com sucesso");
            })
            .catch(error => {
                console.error('Erro ao exportar dados:', error);
                this.toast.error("Erro ao exportar dados");
            })
            .finally(() => {
                this.loading = false;
            });
        },
        downloadCSV(data, filename) {
            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(value || 0);
        },
        formatNumber(value) {
            return new Intl.NumberFormat('pt-BR').format(value || 0);
        },
        formatDate(date) {
            return new Date(date).toLocaleDateString('pt-BR');
        },
        formatMonth(monthString) {
            const [year, month] = monthString.split('-');
            const date = new Date(year, month - 1);
            return date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        },
        getAgeGroupLabel(ageGroup) {
            const labels = {
                'first_week': 'Primeira Semana',
                'first_month': 'Primeiro Mês',
                'first_6_months': 'Primeiros 6 Meses',
                'established': 'Estabelecido'
            };
            return labels[ageGroup] || ageGroup;
        },
        getAgeGroupClass(ageGroup) {
            const classes = {
                'first_week': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'first_month': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                'first_6_months': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'established': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
            };
            return classes[ageGroup] || '';
        },
        getAuditStatusLabel(status) {
            const labels = {
                'pending': 'Pendente',
                'approved': 'Aprovado',
                'rejected': 'Rejeitado',
                'reviewing': 'Em Análise'
            };
            return labels[status] || status;
        },
        getAuditStatusClass(status) {
            const classes = {
                'pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
                'approved': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                'rejected': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                'reviewing': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            };
            return classes[status] || '';
        }
    }
}
</script>